import { Link } from "react-router-dom";
import { ArrowRight, Brain, Users, ShoppingBag, Dumbbell, Apple, Trophy, Check, Globe, Heart, Sparkles, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-nexus-4/artifacts/5uryz4az_ChatGPT%20Image%20Feb%208%2C%202026%2C%2003_09_14%20PM.png";
const HERO_IMG = "https://images.unsplash.com/photo-1762169786069-be5b81cd42b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxvdXRkb29yJTIwZml0bmVzcyUyMHJ1bm5pbmclMjBncm91cCUyMG5hdHVyZXxlbnwwfHx8fDE3NzA5MTk5OTd8MA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMG1 = "https://customer-assets.emergentagent.com/job_1097075e-32a9-4c88-942d-3d6dd2af456f/artifacts/lm62h3vu_Screenshot_10-3-2026_21316_www.bing.com.jpeg";
const COMMUNITY_IMG2 = "https://images.unsplash.com/photo-1758875569256-f37c438cac65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjBsaWZ0aW5nJTIwd2VpZ2h0cyUyMGd5bSUyMGJhcmJlbGx8ZW58MHx8fHwxNzczMTcwNjQyfDA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  return (
    <div className="min-h-screen bg-nature-gradient" data-testid="home-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-nature-hero" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-24 md:pt-20 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 bg-[#F2F9F6] rounded-full px-4 py-2 text-sm font-medium text-[#0B6E4F]">
                <Sparkles className="h-4 w-4" />
                <span>The Natural-First Health Platform</span>
              </div>
              <h1 className="font-['Outfit'] text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-[#0F172A]">
                Unite.<br />
                <span className="text-[#0B6E4F]">Transform.</span><br />
                Thrive.
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-xl">
                Join a global community focused on Health and Fitness. Personalized AI plans, monthly programs, and a marketplace built for your growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/community">
                  <Button data-testid="hero-join-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
                    Join the Community <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/auth?mode=register">
                  <Button data-testid="hero-transform-btn" variant="outline" className="border-2 border-[#0B6E4F] text-[#0B6E4F] hover:bg-[#0B6E4F] hover:text-white rounded-full px-8 py-6 text-base font-semibold transition-all">
                    Start Your Transformation
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img src={HERO_IMG} alt="Fitness community" className="w-full h-[400px] md:h-[520px] object-cover" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass rounded-2xl p-5">
                    <p className="text-sm font-semibold text-[#0F172A]">Better Health. Smarter Fitness.</p>
                    <p className="text-xs text-slate-500 mt-1">Powered by Vita Nex</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#D4F4DD] rounded-full opacity-60 blur-xl" />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#0B6E4F]/10 rounded-full blur-xl" />
            </div>
          </div>
        </div>
        {/* Subtle background glow */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 opacity-30" style={{ background: "radial-gradient(circle at 80% 20%, rgba(212, 244, 221, 0.5) 0%, rgba(255,255,255,0) 60%)" }} />
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-nature-section relative" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-[#0B6E4F] mb-3">Core Features</p>
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-semibold tracking-tight text-[#0F172A]">
              Everything You Need to Thrive
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Users, title: "Global Communities", desc: "Connect with fitness enthusiasts and health-conscious individuals worldwide. Share, learn, and grow together.", color: "bg-[#0B6E4F]" },
              { icon: Brain, title: "Vita AI", desc: "AI-powered personalized meal plans, fitness programs, grocery lists, and calorie tracking tailored to your goals.", color: "bg-[#1B3A5C]" },
              { icon: ShoppingBag, title: "VitaMarket", desc: "Buy and sell health products, fitness equipment, supplements, and wellness items in our secure marketplace.", color: "bg-[#0B6E4F]" },
              { icon: Dumbbell, title: "Fitness Programs", desc: "4-week personalized workout programs for home or gym, designed to match your fitness level and goals.", color: "bg-[#1B3A5C]" },
              { icon: Apple, title: "Smart Meal Plans", desc: "Budget-friendly meal plans with daily calorie breakdowns, weekly grocery lists, and nutritional balance.", color: "bg-[#0B6E4F]" },
              { icon: Trophy, title: "Challenges & Badges", desc: "Join monthly challenges, earn achievement badges, and track your transformation journey with the community.", color: "bg-[#1B3A5C]" },
            ].map((f, i) => (
              <div key={i} className="bg-nature-card shadow-sm hover:shadow-md transition-all rounded-2xl p-8 group" data-testid={`feature-card-${i}`}>
                <div className={`${f.color} w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <f.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-[#0F172A] mb-3">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Preview */}
      <section className="py-24" data-testid="community-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <p className="text-sm font-medium tracking-wide uppercase text-[#0B6E4F]">Global Communities</p>
              <h2 className="font-['Outfit'] text-4xl md:text-5xl font-semibold tracking-tight text-[#0F172A]">
                Your Tribe Awaits
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Create your profile, share your journey, and connect with accountability partners around the world.
              </p>
              <div className="space-y-4 pt-4">
                {[
                  { icon: Dumbbell, text: "Fitness Community - Workouts, transformations, challenges" },
                  { icon: Heart, text: "Health Community - Recipes, wellness, mental health" },
                  { icon: Shield, text: "Safe, inclusive space for everyone" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#F2F9F6] flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-[#0B6E4F]" />
                    </div>
                    <p className="text-slate-600 pt-2">{item.text}</p>
                  </div>
                ))}
              </div>
              <Link to="/community">
                <Button className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-8 py-3 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all mt-4">
                  Explore Communities <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 items-center">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src={COMMUNITY_IMG1} alt="Fitness" className="w-full h-72 object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src={COMMUNITY_IMG2} alt="Health" className="w-full h-72 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-[#F2F9F6] relative grain" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-[#0B6E4F] mb-3">Pricing Plans</p>
            <h2 className="font-['Outfit'] text-4xl md:text-5xl font-semibold tracking-tight text-[#0F172A]">
              Invest in Your Health
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Trial */}
            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all" data-testid="plan-free">
              <p className="text-sm font-medium text-[#0B6E4F] uppercase tracking-wide">Free Trial</p>
              <div className="mt-4 mb-6">
                <span className="font-['Outfit'] text-4xl font-bold text-[#0F172A]">$0</span>
                <span className="text-slate-500 text-sm ml-1">/3 days</span>
              </div>
              <ul className="space-y-3 mb-8">
                {["Basic Vita AI access", "Non-personalized meal plans", "View-only community access", "Limited AI interaction"].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                    <Check className="h-4 w-4 text-[#0B6E4F] mt-0.5 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Link to="/auth?mode=register">
                <Button data-testid="plan-free-btn" variant="outline" className="w-full border-2 border-[#0B6E4F] text-[#0B6E4F] hover:bg-[#0B6E4F] hover:text-white rounded-full font-semibold transition-all">
                  Start Free Trial
                </Button>
              </Link>
            </div>

            {/* Premium */}
            <div className="bg-[#0B6E4F] text-white rounded-2xl p-8 shadow-xl relative ring-2 ring-[#0B6E4F] ring-offset-4" data-testid="plan-premium">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4F4DD] text-[#0B6E4F] text-xs font-bold px-4 py-1 rounded-full">
                MOST POPULAR
              </div>
              <p className="text-sm font-medium text-[#D4F4DD] uppercase tracking-wide">Premium</p>
              <div className="mt-4 mb-6">
                <span className="font-['Outfit'] text-4xl font-bold">$7</span>
                <span className="text-white/70 text-sm ml-1">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {["Full community access", "Personalized AI meal plans", "Monthly fitness programs", "Marketplace access", "Exclusive challenges", "Community badges"].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                    <Check className="h-4 w-4 text-[#D4F4DD] mt-0.5 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Link to="/pricing">
                <Button data-testid="plan-premium-btn" className="w-full bg-white text-[#0B6E4F] hover:bg-[#D4F4DD] rounded-full font-semibold shadow-lg transition-all">
                  Start for $7/month
                </Button>
              </Link>
            </div>

            {/* Super Premium */}
            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all" data-testid="plan-super-premium">
              <p className="text-sm font-medium text-[#1B3A5C] uppercase tracking-wide">Super Premium</p>
              <div className="mt-4 mb-6">
                <span className="font-['Outfit'] text-4xl font-bold text-[#0F172A]">$16</span>
                <span className="text-slate-500 text-sm ml-1">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {["Everything in Premium", "Day-to-day meal schedule", "Daily workout breakdown", "Daily calorie tracking", "AI performance analysis", "Automated reminders"].map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                    <Check className="h-4 w-4 text-[#1B3A5C] mt-0.5 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Link to="/pricing">
                <Button data-testid="plan-super-btn" className="w-full bg-[#1B3A5C] hover:bg-[#152d48] text-white rounded-full font-semibold shadow-lg transition-all">
                  Start for $16/month
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24" data-testid="vision-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Globe className="h-12 w-12 text-[#0B6E4F] mx-auto mb-6" />
          <h2 className="font-['Outfit'] text-4xl md:text-5xl font-semibold tracking-tight text-[#0F172A] mb-6">
            Building the World's Most Connected Health Ecosystem.
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-10">
            VittaNex exists to unite people globally through fitness, natural health, and intelligent technology — creating a place where everyone belongs.
          </p>
          <Link to="/auth?mode=register">
            <Button className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-10 py-6 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
              Join the Movement <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-nature-footer text-white py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="VittaNex" className="h-10 w-10 rounded-full" />
                <span className="font-['Outfit'] font-bold text-xl">VittaNex</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Better Health. Smarter Fitness. Powered by Vita Nex.
              </p>
            </div>
            <div>
              <p className="font-semibold text-sm mb-4">Platform</p>
              <div className="space-y-3">
                {["Community", "Vita AI", "Marketplace", "Pricing"].map((l) => (
                  <Link key={l} to={`/${l.toLowerCase().replace(" ", "-")}`} className="block text-sm text-slate-400 hover:text-white transition-colors">{l}</Link>
                ))}
              </div>
            </div>
            <div>
              <p className="font-semibold text-sm mb-4">Company</p>
              <div className="space-y-3">
                {["About", "Blog", "Careers", "Contact"].map((l) => (
                  <a key={l} href="#" className="block text-sm text-slate-400 hover:text-white transition-colors">{l}</a>
                ))}
              </div>
            </div>
            <div>
              <p className="font-semibold text-sm mb-4">Legal</p>
              <div className="space-y-3">
                {["Privacy Policy", "Terms of Service", "Cookie Policy"].map((l) => (
                  <a key={l} href="#" className="block text-sm text-slate-400 hover:text-white transition-colors">{l}</a>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center">
            <p className="text-sm text-slate-500">2026 VittaNex. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
